import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigation } from '../context/NavigationContext';
import LiveJobsBoard, { VISIT_HAPPENED_STATUSES } from '../components/rider/LiveJobsBoard';
import AddBoltOnDialog from '../components/rider/AddBoltOnDialog';
import CloseStopDialog from '../components/rider/CloseStopDialog';
import {
  assignJobToRider,
  addBoltOnToDispatched,
  assignTripToRider,
  getBoltOnCandidates,
  collectRiderCash,
  endRiderShift,
  getRiderAdminCash,
  getRiderAdminIssues,
  getRiderAdminOverview,
  getRiderAdminVisits,
  getRiderIssueAudioBlob,
  getRiderAssignmentJobs,
  cancelRiderJob,
  completeRiderJob,
  listAssignableRiders,
  resolveRiderIssue,
} from '../api/riderAssignment';

function formatScheduled(date, time) {
  if (!date) return '-';
  const d = new Date(date + 'T00:00:00');
  const dateStr = isNaN(d.getTime())
    ? date
    : d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' });
  return time ? `${dateStr} · ${time}` : dateStr;
}

function errorMessage(err, fallback) {
  const detail = err.response?.data?.detail;
  if (!detail) return err.message || fallback;
  if (typeof detail === 'string') return detail;
  if (detail.error) return typeof detail.error === 'string' ? detail.error : JSON.stringify(detail.error);
  if (detail.message) return typeof detail.message === 'string' ? detail.message : JSON.stringify(detail.message);
  return JSON.stringify(detail);
}

// Money arrives as a decimal string and stays one. Summing happens in integer
// minor units so a bill total is never round-tripped through a float.
function sumMoney(values) {
  const paise = values.reduce((total, raw) => {
    const [rupees, fraction = ''] = String(raw ?? '0').trim().split('.');
    const minor = (fraction + '00').slice(0, 2);
    return total + (parseInt(rupees, 10) || 0) * 100 + (parseInt(minor, 10) || 0);
  }, 0);
  return `${Math.trunc(paise / 100)}.${String(Math.abs(paise % 100)).padStart(2, '0')}`;
}

const AVAILABILITY_STYLES = {
  AVAILABLE: 'bg-green-50 text-green-700 border-green-200',
  BUSY: 'bg-blue-50 text-blue-700 border-blue-200',
  ASSIGNMENT_OFFERED: 'bg-amber-50 text-amber-700 border-amber-200',
  RETURNING: 'bg-purple-50 text-purple-700 border-purple-200',
  UNAVAILABLE: 'bg-gray-50 text-gray-500 border-gray-200',
};

function AvailabilityBadge({ status }) {
  const style = AVAILABILITY_STYLES[status] || 'bg-gray-50 text-gray-500 border-gray-200';
  return (
    <span className={`inline-block text-xs font-semibold px-2 py-0.5 rounded border ${style}`}>
      {(status || 'UNKNOWN').replace(/_/g, ' ')}
    </span>
  );
}

function formatAgo(raw) {
  if (!raw) return null;
  const then = new Date(raw);
  if (isNaN(then.getTime())) return raw;
  const minutes = Math.round((Date.now() - then.getTime()) / 60000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  return `${hours}h ${minutes % 60}m ago`;
}

function formatWorked(minutes) {
  if (minutes === null || minutes === undefined) return '-';
  const hours = Math.floor(minutes / 60);
  return hours > 0 ? `${hours}h ${minutes % 60}m` : `${minutes}m`;
}

function formatTime(raw) {
  if (!raw) return '-';
  const d = new Date(raw);
  if (isNaN(d.getTime())) return raw;
  return d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true });
}

function CountsStrip({ overview }) {
  if (!overview) return null;
  const counts = Object.entries(overview.counts || {});
  return (
    <div className="mb-4 flex flex-wrap items-center gap-2 text-sm">
      {counts.length === 0 && (
        <span className="text-gray-400 italic">No riders on shift.</span>
      )}
      {counts.map(([statusName, count]) => (
        <span
          key={statusName}
          className={`px-3 py-1.5 rounded border font-semibold ${
            AVAILABILITY_STYLES[statusName] || 'bg-gray-50 text-gray-500 border-gray-200'
          }`}
        >
          {count} {statusName.replace(/_/g, ' ').toLowerCase()}
        </span>
      ))}
      <span className="px-3 py-1.5 rounded border border-gray-200 bg-white text-gray-700">
        Cash in hand <strong className="text-gray-900">₹{overview.cash_in_hand_total}</strong>
      </span>
      {overview.open_issue_count > 0 && (
        <span className="px-3 py-1.5 rounded border border-red-200 bg-red-50 text-zoddle-pink font-semibold">
          {overview.open_issue_count} open issue{overview.open_issue_count !== 1 ? 's' : ''}
        </span>
      )}
    </div>
  );
}

function RiderBoard({ overview, onEndShift, endingRiderId }) {
  const riders = overview?.riders || [];
  if (riders.length === 0) {
    return (
      <div className="text-gray-500 italic py-8 text-center bg-white border border-gray-200 rounded">
        No riders are on shift right now.
      </div>
    );
  }
  return (
    <div className="overflow-x-auto bg-white border border-gray-200 rounded">
      <table className="w-full text-sm">
        <thead className="bg-gray-50 text-xs text-gray-500 uppercase tracking-wide">
          <tr>
            <th className="text-left px-4 py-3 font-semibold">Rider</th>
            <th className="text-left px-4 py-3 font-semibold">Availability</th>
            <th className="text-left px-4 py-3 font-semibold">Worked</th>
            <th className="text-left px-4 py-3 font-semibold">Current job</th>
            <th className="text-left px-4 py-3 font-semibold">Last seen</th>
            <th className="text-right px-4 py-3 font-semibold">Cash in hand</th>
            <th className="text-right px-4 py-3 font-semibold"></th>
          </tr>
        </thead>
        <tbody>
          {riders.map(rider => (
            <tr key={rider.rider_id} className="border-t border-gray-100">
              <td className="px-4 py-3">
                <div className="font-semibold text-gray-800">{rider.full_name || '-'}</div>
                <div className="text-xs text-gray-500">{rider.phone_e164 || ''}</div>
              </td>
              <td className="px-4 py-3">
                <AvailabilityBadge status={rider.availability} />
                {rider.on_break && (
                  <div className="text-xs text-amber-600 font-semibold mt-1">On break</div>
                )}
              </td>
              <td className="px-4 py-3 text-gray-700">{formatWorked(rider.worked_minutes)}</td>
              <td className="px-4 py-3">
                {rider.active_job ? (
                  <>
                    <div className="font-mono text-gray-800">{rider.active_job.source_job_id}</div>
                    <div className="text-xs text-gray-500">
                      {(rider.active_job.status || '').replace(/_/g, ' ')}
                    </div>
                  </>
                ) : (
                  <span className="text-gray-400">Free</span>
                )}
              </td>
              <td className="px-4 py-3">
                {/* A rider on shift with no location is not hidden — that is the
                    state worth noticing. */}
                {rider.location ? (
                  <span className="text-gray-700">{formatAgo(rider.location.recorded_at)}</span>
                ) : (
                  <span className="text-amber-600 font-semibold">Not reporting</span>
                )}
              </td>
              <td className="px-4 py-3 text-right">
                <div className="font-semibold text-gray-800">₹{rider.cash_in_hand}</div>
                {rider.cash_bill_count > 0 && (
                  <div className="text-xs text-gray-500">
                    {rider.cash_bill_count} bill{rider.cash_bill_count !== 1 ? 's' : ''}
                  </div>
                )}
              </td>
              <td className="px-4 py-3 text-right">
                <button
                  onClick={() => onEndShift(rider)}
                  disabled={endingRiderId === rider.rider_id}
                  className="text-sm font-semibold text-zoddle-pink hover:text-red-700 disabled:opacity-50"
                >
                  {endingRiderId === rider.rider_id ? 'Ending...' : 'End Shift'}
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function CashHolding({ holding, selected, onToggleBill, onToggleAll, onCollect, busy }) {
  const billIds = holding.bills.map(bill => bill.checkout_id);
  const chosen = billIds.filter(id => selected[id]);
  const allChosen = billIds.length > 0 && chosen.length === billIds.length;
  const chosenTotal = sumMoney(
    holding.bills.filter(bill => selected[bill.checkout_id]).map(bill => bill.amount)
  );

  return (
    <div className="bg-white border border-gray-200 rounded mb-4">
      <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 border-b border-gray-100">
        <div>
          <div className="font-semibold text-gray-800">{holding.full_name || 'Rider'}</div>
          <div className="text-xs text-gray-500">{holding.phone_e164 || ''}</div>
        </div>
        <div className="flex items-center gap-4">
          <div className="text-right">
            <div className="text-xs text-gray-400 uppercase tracking-wide">Carrying</div>
            <div className="text-lg font-bold text-gray-900">₹{holding.total}</div>
          </div>
          <button
            onClick={() => onToggleAll(billIds, !allChosen)}
            className="text-sm font-semibold text-gray-500 hover:text-zoddle-green"
          >
            {allChosen ? 'Clear' : 'Select all'}
          </button>
        </div>
      </div>

      <div className="divide-y divide-gray-100">
        {holding.bills.map(bill => (
          <label
            key={bill.checkout_id}
            className="flex items-center gap-3 px-4 py-2.5 cursor-pointer hover:bg-gray-50"
          >
            <input
              type="checkbox"
              checked={!!selected[bill.checkout_id]}
              onChange={() => onToggleBill(bill.checkout_id)}
              className="w-4 h-4 accent-zoddle-green"
            />
            <div className="min-w-0">
              <div className="font-mono text-sm text-gray-800">{bill.source_job_id || '-'}</div>
              <div className="text-xs text-gray-400 font-mono">{bill.source_order_id || ''}</div>
            </div>
            {/* ZOCS never recorded this payment. A sync anomaly, not a collection
                problem — flagged distinctly so the counter chases the right thing. */}
            {bill.zocs_unaware && (
              <span className="text-xs font-semibold px-2 py-0.5 rounded border border-amber-300 bg-amber-50 text-amber-700">
                ⚠ Not in ZOCS books
              </span>
            )}
            <div className="ml-auto text-right">
              <div className="font-semibold text-gray-800">₹{bill.amount}</div>
              <div className="text-xs text-gray-400">{formatTime(bill.paid_at)}</div>
            </div>
          </label>
        ))}
      </div>

      <div className="px-4 py-3 border-t border-gray-100 flex justify-end">
        <button
          onClick={() => onCollect(holding, chosen, chosenTotal)}
          disabled={chosen.length === 0 || busy}
          className="text-sm font-semibold bg-zoddle-green text-white px-4 py-2 rounded hover:opacity-90 disabled:opacity-40"
        >
          {busy
            ? 'Collecting...'
            : `Collect ₹${chosenTotal} from ${(holding.full_name || 'rider').split(' ')[0]}`}
        </button>
      </div>
    </div>
  );
}

function EntryCodesBoard({ visits }) {
  // Not gated on status: a rider requesting this code stays at ARRIVED until the code
  // is confirmed, so entry_decline_code alone is what says a call is expected.
  const pending = (visits || []).filter(v => v.entry_decline_code);
  if (pending.length === 0) {
    return (
      <div className="text-gray-500 italic py-8 text-center bg-white border border-gray-200 rounded">
        No riders are waiting on an entry-decline code right now.
      </div>
    );
  }
  return (
    <div className="space-y-3">
      {pending.map(visit => (
        <div
          key={visit.source_job_id}
          className="bg-amber-50 border border-amber-200 rounded p-4 flex items-center justify-between gap-4"
        >
          <div>
            <div className="font-mono text-gray-800">{visit.source_job_id}</div>
            <div className="text-sm text-gray-700 mt-0.5">
              {visit.customer_name || 'Not in ZOCS'}
              {visit.customer_area ? ` · ${visit.customer_area}` : ''}
            </div>
            <div className="text-xs text-gray-500 mt-0.5">
              Rider: {visit.rider_name || '-'}
            </div>
          </div>
          <div className="text-2xl font-bold text-amber-800 tracking-widest">
            {visit.entry_decline_code}
          </div>
        </div>
      ))}
    </div>
  );
}

function IssuesBoard({ issues, onChanged }) {
  const [notes, setNotes] = useState({});
  const [busyId, setBusyId] = useState(null);
  const [audioUrls, setAudioUrls] = useState({});
  const audioUrlsRef = useRef([]);
  const [error, setError] = useState(null);

  useEffect(() => () => {
    audioUrlsRef.current.forEach(url => URL.revokeObjectURL(url));
  }, []);

  async function play(issue) {
    setBusyId(issue.issue_id);
    setError(null);
    try {
      const blob = await getRiderIssueAudioBlob(issue.issue_id);
      const url = URL.createObjectURL(blob);
      audioUrlsRef.current.push(url);
      setAudioUrls(current => ({ ...current, [issue.issue_id]: url }));
    } catch (err) {
      setError(errorMessage(err, 'Voice note is unavailable'));
    } finally {
      setBusyId(null);
    }
  }

  async function resolve(issue) {
    const note = (notes[issue.issue_id] || '').trim();
    if (!note) {
      setError('A resolution note is required.');
      return;
    }
    setBusyId(issue.issue_id);
    setError(null);
    try {
      await resolveRiderIssue(issue.issue_id, note);
      await onChanged();
    } catch (err) {
      setError(errorMessage(err, 'Could not resolve issue'));
    } finally {
      setBusyId(null);
    }
  }

  if (!issues?.length) return (
    <div className="text-gray-500 italic py-8 text-center bg-white border border-gray-200 rounded">
      No rider visit issues reported.
    </div>
  );
  return (
    <div className="space-y-3">
      {error && <div className="text-sm text-zoddle-pink">{error}</div>}
      {issues.map(issue => (
        <div key={issue.issue_id} className="bg-white border border-gray-200 rounded p-4">
          <div className="flex flex-wrap justify-between gap-3">
            <div>
              <div className="font-semibold text-gray-800">
                {issue.source_job_id} · {issue.customer_name}
              </div>
              <div className="text-sm text-gray-600 mt-1">
                {issue.category.replace(/_/g, ' ')} — {issue.description}
              </div>
              <div className="text-xs text-gray-400 mt-1">
                {issue.rider_name || issue.rider_id} · {new Date(issue.reported_at).toLocaleString('en-IN')} · {issue.sync_state}
              </div>
              {(issue.outcome || issue.outcome_reason_code) && (
                <div className="text-xs text-gray-500 mt-1">
                  Outcome: {issue.outcome || 'pending'} · {issue.outcome_reason_code || ''} {issue.outcome_reason_text || ''}
                </div>
              )}
            </div>
            <span className="text-xs font-semibold text-gray-500">{issue.status}</span>
          </div>
          {issue.voice_note_available && !audioUrls[issue.issue_id] && (
            <button
              onClick={() => play(issue)}
              disabled={busyId === issue.issue_id}
              className="mt-3 text-sm font-semibold text-zoddle-green"
            >
              {busyId === issue.issue_id ? 'Loading audio...' : 'Load voice note'}
            </button>
          )}
          {audioUrls[issue.issue_id] && (
            <audio className="mt-3 w-full" controls src={audioUrls[issue.issue_id]} />
          )}
          {issue.status !== 'RESOLVED' ? (
            <div className="mt-3 flex flex-wrap gap-2">
              <input
                value={notes[issue.issue_id] || ''}
                onChange={e => setNotes(current => ({ ...current, [issue.issue_id]: e.target.value }))}
                placeholder="Resolution note"
                className="flex-1 min-w-64 text-sm border border-gray-200 rounded px-3 py-2"
              />
              <button
                onClick={() => resolve(issue)}
                disabled={busyId === issue.issue_id}
                className="text-sm font-semibold bg-zoddle-green text-white px-4 py-2 rounded disabled:opacity-40"
              >
                Resolve
              </button>
            </div>
          ) : issue.resolution_note && (
            <div className="mt-3 text-sm text-gray-500">Resolved: {issue.resolution_note}</div>
          )}
        </div>
      ))}
    </div>
  );
}

// offer_status is the offer; job_status is how far the visit itself has got.
// The board used to receive only the first, so a rider already out on a job was
// indistinguishable from no rider at all -- which is what hid the Remove button.
function RiderJobState({ job }) {
  if (!job.offer_status && !job.job_status && !job.paid_outside_rider_app && !job.closed_by_office) return null;
  return (
    <div className="flex flex-wrap gap-1 mt-1">
      {job.offer_status && (
        <span className="text-[11px] font-semibold px-1.5 py-0.5 rounded bg-gray-100 text-gray-600">
          {job.offer_status}
        </span>
      )}
      {job.job_status && (
        <span className="text-[11px] font-semibold px-1.5 py-0.5 rounded bg-blue-50 text-blue-700">
          {job.job_status.replace(/_/g, ' ')}
        </span>
      )}
      {job.closed_by_office && (
        <span
          className="text-[11px] font-semibold px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 border border-amber-200"
          title="The office closed this stop. Its bag is still with the rider and comes back at the end of the trip."
        >
          Closed · bag still out
        </span>
      )}
      {job.paid_outside_rider_app && (
        <span
          className="text-[11px] font-semibold px-1.5 py-0.5 rounded bg-pink-50 text-zoddle-pink"
          title="A completed order for this appointment came from outside the rider app, so the rider-app flow will never settle on its own."
        >
          Paid outside rider app
        </span>
      )}
    </div>
  );
}

// Whether another stop can go behind this job is the rider backend's call, made once and in one
// place (`can_add_bolt_on`): the job is accepted and still has its bag, it is the last *live*
// stop (a bolt-on removed before its hand-on no longer counts), and the trip has room. The
// card only renders what it is told, so it can never offer what the command would refuse.
const canAddBoltOn = job => job.can_add_bolt_on === true;

const hasRiderOn = job => !!(job.offer_status || job.job_status || job.rider_id);

/** Why a job cannot be sent yet, or null when it can. Same rules as the table's Assign. */
function jobBlocker(job, mapLink) {
  if (!job.has_pack) return 'No pack yet';
  if (!job.packed_items?.length) return 'Pack is empty';
  if (!mapLink) return 'Needs a map link';
  return null;
}

/**
 * "Plan": the easy way to send work out. Ready jobs on the left as cards — click to add,
 * in the order the rider should visit them. One panel on the right holds the rider and the
 * stops, with a single send button: one job goes as a normal assignment, two or more as one
 * trip. Below, who is out with what. Same calls as the table tab; only the layout differs.
 */
// Most customers one trip may hold. ZOCS and the rider backend enforce the same limit.
const MAX_TRIP_STOPS = 5;

function localDate(offsetDays = 0) {
  const d = new Date();
  d.setDate(d.getDate() + offsetDays);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function isTodayDate(date) {
  return !!date && date === localDate(0);
}

// "10:00 AM - 11:00 AM" -> minutes after midnight of the slot's start, for sorting.
function slotMinutes(time) {
  const match = /(\d{1,2}):(\d{2})\s*(AM|PM)/i.exec(time || '');
  if (!match) return 24 * 60;
  const hour = Number(match[1]) % 12 + (match[3].toUpperCase() === 'PM' ? 12 : 0);
  return hour * 60 + Number(match[2]);
}

const DATE_FILTERS = [
  { key: 'all', label: 'All dates', test: () => true },
  { key: 'today', label: 'Today', test: date => date === localDate(0) },
  { key: 'tomorrow', label: 'Tomorrow', test: date => date === localDate(1) },
];

// The slot leads a card: staff plan the day by time, then by place.
function SlotLabel({ job }) {
  const dateStr = formatScheduled(job.scheduled_date).split(' · ')[0];
  const today = isTodayDate(job.scheduled_date);
  return (
    <div className="min-w-0">
      <div className="text-base font-bold text-gray-900 tabular-nums">{job.scheduled_time || '—'}</div>
      <div className="mt-0.5 flex items-center gap-1.5 text-xs text-gray-500">
        {today ? (
          <span className="rounded bg-zoddle-green/10 px-1.5 py-px font-semibold uppercase tracking-wide text-zoddle-green">Today</span>
        ) : null}
        <span>{dateStr}</span>
      </div>
    </div>
  );
}

function ChildChips({ children }) {
  if (!children?.length) return null;
  return children.map((child, index) => {
    const gender = (child.gender || '').toLowerCase();
    const tone = gender === 'girl'
      ? 'bg-pink-50 text-pink-700 ring-pink-100'
      : gender === 'boy'
        ? 'bg-sky-50 text-sky-700 ring-sky-100'
        : 'bg-gray-100 text-gray-600 ring-gray-200';
    return (
      <span
        key={child.appointment_child_id || index}
        className={`rounded-md px-2 py-0.5 text-xs font-semibold ring-1 ring-inset ${tone}`}
      >
        {gender ? gender.charAt(0).toUpperCase() + gender.slice(1) : 'Child'} · {child.size || '?'}
      </span>
    );
  });
}

// Copies a value without selecting the card it sits on.
function CopyButton({ value, label }) {
  const [copied, setCopied] = useState(false);
  const copy = async e => {
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1200);
    } catch {
      // Clipboard blocked; nothing else to do.
    }
  };
  return (
    <button
      type="button"
      onClick={copy}
      onKeyDown={e => e.stopPropagation()}
      title={`Copy ${label}`}
      aria-label={`Copy ${label}`}
      className="rounded px-1 text-[11px] font-medium text-gray-400 hover:bg-gray-100 hover:text-gray-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-zoddle-green"
    >
      {copied ? 'Copied' : 'Copy'}
    </button>
  );
}

function SearchIcon() {
  return (
    <svg aria-hidden="true" viewBox="0 0 20 20" className="h-4 w-4 text-gray-400" fill="none" stroke="currentColor" strokeWidth="1.8">
      <circle cx="9" cy="9" r="5.5" />
      <path d="M13.5 13.5 17 17" strokeLinecap="round" />
    </svg>
  );
}

/**
 * "Assign": the easy way to send work out. Jobs on the left as cards — click to add, in
 * the order the rider should visit them. The right panel draws that order as the route the
 * bag takes: out of the warehouse, customer by customer (a bolt-on hanging off the stop
 * whose bag it uses), and back. Same calls as the table tab; only the layout differs.
 */
function PlanBoard({
  jobs, rowById, boltOnCandidates, boltOnPicks, onToggleBoltOn,
  riders, riderChoice, setRiderChoice, picks, onToggle, onMove, onSend, onRemove, onAddBoltOn,
  mapLinks, setMapLinks, blockers, busyId,
}) {
  const [addingTo, setAddingTo] = useState(null);
  const [query, setQuery] = useState('');
  const [view, setView] = useState('packed');
  const [dateFilter, setDateFilter] = useState('all');
  // Cards whose map link is open for editing even though one is already saved.
  const [editingLinks, setEditingLinks] = useState({});
  const q = query.trim().toLowerCase();
  const dateTest = DATE_FILTERS.find(filter => filter.key === dateFilter).test;
  const matches = job => dateTest(job.scheduled_date) && (!q || [job.customer_name, job.area, job.zcp_appointment_uid, job.pincode]
    .some(value => (value || '').toLowerCase().includes(q)));
  const bySlot = (a, b) => (a.scheduled_date || '').localeCompare(b.scheduled_date || '')
    || slotMinutes(a.scheduled_time) - slotMinutes(b.scheduled_time);

  // A queued bolt-on is never "ready": it has no pack to send, only a rider to ride with.
  const ready = jobs.filter(job => !hasRiderOn(job) && job.operational_status !== 'queued');
  const out = jobs.filter(job => hasRiderOn(job));
  const shown = (view === 'packed' ? ready : boltOnCandidates).filter(matches).slice().sort(bySlot);
  const full = picks.length >= MAX_TRIP_STOPS;
  const byRider = out.reduce((groups, job) => {
    const key = job.rider_id || job.rider_name || 'unknown';
    (groups[key] = groups[key] || { name: job.rider_name || 'Rider offered', phone: job.rider_phone, jobs: [] }).jobs.push(job);
    return groups;
  }, {});
  const rider = riders.find(r => r.staff_id === riderChoice);
  const sending = busyId === 'trip' || (picks.length === 1 && busyId === picks[0]);
  const bags = picks.filter(id => !boltOnPicks.includes(id)).length;

  const sendLabel = sending
    ? 'Sending…'
    : picks.length === 0
      ? 'Send to rider'
      : picks.length === 1
        ? `Send to ${rider ? rider.full_name : 'rider'}`
        : `Send trip to ${rider ? rider.full_name : 'rider'}`;

  return (
    <div className="grid items-start gap-6 lg:grid-cols-[minmax(0,1fr)_380px]">
      <div className="min-w-0 space-y-8">
        <section>
          <div className="mb-4 flex flex-wrap items-center gap-3 rounded-xl border border-gray-200 bg-white p-2">
            <div role="tablist" aria-label="Which jobs" className="inline-flex rounded-lg bg-gray-100 p-1">
              {[
                { key: 'packed', label: 'Packed', count: ready.length },
                { key: 'bolt-on', label: 'No pack yet', count: boltOnCandidates.length },
              ].map(tab => (
                <button
                  key={tab.key}
                  role="tab"
                  aria-selected={view === tab.key}
                  onClick={() => setView(tab.key)}
                  className={`rounded-md px-3.5 py-1.5 text-sm font-semibold transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-zoddle-green ${
                    view === tab.key ? 'bg-zoddle-green text-white shadow-sm' : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  {tab.label}
                  <span className={`ml-1.5 tabular-nums ${view === tab.key ? 'text-white/80' : 'text-gray-400'}`}>{tab.count}</span>
                </button>
              ))}
            </div>
            <div className="flex items-center gap-1" role="group" aria-label="Date">
              {DATE_FILTERS.map(filter => (
                <button
                  key={filter.key}
                  onClick={() => setDateFilter(filter.key)}
                  aria-pressed={dateFilter === filter.key}
                  className={`rounded-full border px-3 py-1 text-xs font-semibold transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-zoddle-green ${
                    dateFilter === filter.key
                      ? 'border-gray-900 bg-gray-900 text-white'
                      : 'border-gray-200 text-gray-600 hover:border-gray-400'
                  }`}
                >
                  {filter.label}
                </button>
              ))}
            </div>
            <label className="relative ml-auto">
              <span className="pointer-events-none absolute inset-y-0 left-2.5 flex items-center"><SearchIcon /></span>
              <input
                value={query}
                onChange={e => setQuery(e.target.value)}
                placeholder="Name, area or UID"
                aria-label="Search jobs"
                className="w-60 max-w-full rounded-lg border border-gray-200 bg-white py-2 pl-8 pr-3 text-sm focus:border-zoddle-green focus:outline-none"
              />
            </label>
          </div>

          {view === 'bolt-on' && (
            <p className="mb-4 max-w-2xl text-sm text-gray-500">
              These have no bag. Add one after a packed job and it gets that customer&rsquo;s leftovers on the road.
              {picks.length === 0 && <span className="font-medium text-gray-700"> Pick a packed job first.</span>}
            </p>
          )}

          {shown.length === 0 ? (
            <div className="rounded-xl border border-dashed border-gray-200 bg-white px-6 py-12 text-center text-sm text-gray-400">
              {q
                ? 'Nothing matches that search.'
                : view === 'packed'
                  ? 'Every packed job already has a rider.'
                  : 'No unpacked orders for today.'}
            </div>
          ) : (
            <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
              {shown.map(job => {
                const isBoltOnCard = view === 'bolt-on';
                const position = picks.indexOf(job.appointment_id);
                const picked = position >= 0;
                const link = (mapLinks[job.appointment_id] || '').trim();
                const blocker = isBoltOnCard ? null : jobBlocker(job, link);
                const packReady = job.has_pack && job.packed_items?.length > 0;
                const selectable = picked || (!full && (isBoltOnCard ? picks.length > 0 : packReady));
                const toggle = () => {
                  if (!selectable) return;
                  if (isBoltOnCard) onToggleBoltOn(job.appointment_id);
                  else onToggle(job.appointment_id);
                };
                return (
                  <div
                    key={job.appointment_id}
                    role="button"
                    tabIndex={selectable ? 0 : -1}
                    aria-pressed={picked}
                    aria-disabled={!selectable}
                    onClick={toggle}
                    onKeyDown={e => {
                      if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggle(); }
                    }}
                    title={
                      !selectable && full
                        ? `A trip holds up to ${MAX_TRIP_STOPS} customers`
                        : !selectable && isBoltOnCard
                          ? 'Pick the packed job whose bag it will use first'
                          : ''
                    }
                    className={`group relative flex flex-col gap-3 rounded-xl border bg-white p-4 text-left shadow-sm transition focus:outline-none focus-visible:ring-2 focus-visible:ring-zoddle-green ${
                      isBoltOnCard ? 'border-dashed' : ''
                    } ${
                      picked
                        ? 'border-zoddle-green bg-zoddle-green/[0.04] ring-1 ring-zoddle-green'
                        : selectable
                          ? 'cursor-pointer border-gray-200 hover:border-gray-300 hover:shadow-md'
                          : 'border-gray-200 opacity-55'
                    }`}
                  >
                    <div className="flex items-start justify-between gap-3 border-b border-gray-100 pb-3">
                      <SlotLabel job={job} />
                      {picked ? (
                        <span className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-zoddle-green text-xs font-bold text-white">
                          {position + 1}
                        </span>
                      ) : (
                        <span className="shrink-0 rounded-full bg-gray-100 px-2.5 py-1 text-xs font-semibold text-gray-600 tabular-nums">
                          {isBoltOnCard ? 'No bag' : `${job.item_count ?? 0} pcs`}
                        </span>
                      )}
                    </div>

                    <div className="flex flex-1 flex-col gap-2">
                      <div className="truncate text-base font-semibold text-gray-900" title={job.customer_name || ''}>
                        {job.customer_name || '—'}
                      </div>
                      {job.phone ? (
                        <div className="flex items-center gap-1.5 text-sm">
                          <a
                            href={`tel:${job.phone}`}
                            onClick={e => e.stopPropagation()}
                            className="font-medium text-gray-700 tabular-nums hover:text-gray-900 hover:underline"
                          >
                            {job.phone}
                          </a>
                          <CopyButton value={job.phone} label="phone number" />
                        </div>
                      ) : (
                        <div className="text-sm text-gray-400">No phone</div>
                      )}
                      <div className="flex flex-wrap items-center gap-1.5">
                        <ChildChips children={job.children} />
                        {job.area || job.pincode ? (
                          <span className="text-[11px] text-gray-400">
                            {[job.area, job.pincode].filter(Boolean).join(' · ')}
                          </span>
                        ) : null}
                      </div>
                    </div>

                    <div className="flex flex-col gap-2 border-t border-gray-100 pt-3">
                      {!link || editingLinks[job.appointment_id] ? (
                        <input
                          type="text"
                          value={mapLinks[job.appointment_id] || ''}
                          autoFocus={!!editingLinks[job.appointment_id]}
                          onClick={e => e.stopPropagation()}
                          onKeyDown={e => {
                            e.stopPropagation();
                            if (e.key === 'Enter') setEditingLinks(state => ({ ...state, [job.appointment_id]: false }));
                          }}
                          onBlur={() => setEditingLinks(state => ({ ...state, [job.appointment_id]: false }))}
                          onChange={e => setMapLinks(state => ({ ...state, [job.appointment_id]: e.target.value }))}
                          placeholder="Paste Google Maps link"
                          aria-label={`Map link for ${job.customer_name || 'this job'}`}
                          className={`w-full rounded-lg border px-2.5 py-1.5 text-xs focus:border-zoddle-green focus:outline-none ${
                            link ? 'border-gray-300 bg-white' : 'border-amber-300 bg-amber-50 placeholder:text-amber-700/60'
                          }`}
                        />
                      ) : (
                        <div className="flex items-center gap-3 text-xs">
                          <a
                            href={link}
                            target="_blank"
                            rel="noreferrer"
                            onClick={e => e.stopPropagation()}
                            className="inline-flex items-center gap-1 font-medium text-gray-600 hover:text-gray-900"
                          >
                            <span aria-hidden="true">📍</span>
                            <span className="underline decoration-gray-300">Open map</span>
                          </a>
                          <button
                            type="button"
                            onClick={e => {
                              e.stopPropagation();
                              setEditingLinks(state => ({ ...state, [job.appointment_id]: true }));
                            }}
                            className="font-semibold text-zoddle-green hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-zoddle-green"
                          >
                            Edit
                          </button>
                        </div>
                      )}
                      {blocker && blocker !== 'Needs a map link' ? (
                        <div className="text-xs font-semibold text-red-500">{blocker}</div>
                      ) : null}
                      <div className="flex items-center gap-1.5">
                        <span className="font-mono text-sm font-bold tracking-wide text-gray-900">
                          {job.zcp_appointment_uid || '—'}
                        </span>
                        {job.zcp_appointment_uid ? (
                          <CopyButton value={job.zcp_appointment_uid} label="appointment ID" />
                        ) : null}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </section>

        <section>
          <h3 className="mb-3 text-sm font-bold uppercase tracking-wide text-gray-500">
            Out with riders <span className="font-normal text-gray-400">{out.length}</span>
          </h3>
          {out.length === 0 ? (
            <div className="text-sm text-gray-400">Nobody is out yet.</div>
          ) : (
            <div className="grid gap-3 md:grid-cols-2">
              {Object.entries(byRider).map(([key, group]) => (
                <div key={key} className="rounded-xl border border-gray-200 bg-white p-4">
                  <div className="mb-3 flex items-center gap-3">
                    <span className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-gray-900 text-xs font-bold text-white">
                      {(group.name || '?').charAt(0).toUpperCase()}
                    </span>
                    <div className="min-w-0">
                      <div className="truncate font-semibold text-gray-900">{group.name}</div>
                      <div className="text-xs text-gray-400">{group.phone || ''}</div>
                    </div>
                  </div>
                  <ol className="space-y-2.5 border-l-2 border-gray-100 pl-4">
                    {group.jobs
                      .slice()
                      .sort((a, b) => (a.offer_id || '').localeCompare(b.offer_id || '') || (a.stop_sequence || 0) - (b.stop_sequence || 0))
                      .map(job => (
                        <li key={job.appointment_id} className="relative flex items-start justify-between gap-2 text-sm">
                          <span className="absolute -left-[21px] top-1.5 h-2 w-2 rounded-full bg-zoddle-green" aria-hidden="true" />
                          <div className="min-w-0">
                            <div className="truncate text-gray-800">
                              {job.customer_name || job.zcp_appointment_uid}
                              {job.trip_source_job_ids && (
                                <span className="ml-1.5 text-xs text-gray-400 tabular-nums">
                                  stop {job.stop_sequence}/{job.trip_source_job_ids.length}
                                </span>
                              )}
                            </div>
                            <RiderJobState job={job} />
                          </div>
                          {canAddBoltOn(job) && (
                            <button
                              onClick={() => setAddingTo(job)}
                              disabled={busyId === job.appointment_id}
                              className="shrink-0 rounded px-1 text-xs font-semibold text-gray-600 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-400 disabled:opacity-40"
                            >
                              Add bolt-on
                            </button>
                          )}
                          <button
                            onClick={() => onRemove(job)}
                            disabled={busyId === job.appointment_id}
                            className="shrink-0 rounded px-1 text-xs font-semibold text-zoddle-pink hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-zoddle-pink disabled:opacity-40"
                          >
                            {busyId === job.appointment_id ? 'Removing…' : 'Remove'}
                          </button>
                        </li>
                      ))}
                  </ol>
                </div>
              ))}
            </div>
          )}
        </section>
        {addingTo && (
          <AddBoltOnDialog
            job={addingTo}
            candidates={boltOnCandidates}
            busy={busyId === addingTo.appointment_id}
            onCancel={() => setAddingTo(null)}
            onConfirm={async (job, candidate, link) => {
              const done = await onAddBoltOn(job, candidate, link);
              if (done) setAddingTo(null);
            }}
          />
        )}
      </div>

      <aside className="rounded-xl border border-gray-200 bg-white lg:sticky lg:top-4">
        <div className="border-b border-gray-100 px-5 py-4">
          <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-gray-500" htmlFor="plan-rider">
            Rider
          </label>
          <select
            id="plan-rider"
            value={riderChoice}
            onChange={e => setRiderChoice(e.target.value)}
            className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2.5 text-sm font-medium focus:border-zoddle-green focus:outline-none"
          >
            <option value="">Choose a rider…</option>
            {riders.map(r => (
              <option key={r.staff_id} value={r.staff_id}>{r.full_name}</option>
            ))}
          </select>
          {rider?.phone && <div className="mt-1 text-xs text-gray-400">{rider.phone}</div>}
        </div>

        <div className="px-5 py-4">
          <div className="mb-3 flex items-baseline justify-between text-xs font-semibold uppercase tracking-wide text-gray-500">
            <span>Route</span>
            {picks.length > 0 && (
              <span className={`tabular-nums normal-case tracking-normal ${full ? 'text-amber-700' : 'text-gray-400'}`}>
                {picks.length} of {MAX_TRIP_STOPS} stops{full ? ' · full' : ''}
              </span>
            )}
          </div>
          <ol className="relative">
            <RouteNode label="Warehouse" detail={picks.length ? `${bags} ${bags === 1 ? 'bag' : 'bags'} out` : 'Start'} />
            {picks.length === 0 ? (
              <li className="relative pb-4 pl-8">
                <span className="absolute left-[11px] top-0 h-full border-l-2 border-dashed border-gray-200" aria-hidden="true" />
                <div className="rounded-lg border border-dashed border-gray-200 px-3 py-4 text-sm text-gray-400">
                  Click jobs on the left, in the order to visit them.
                </div>
              </li>
            ) : (
              picks.map((id, index) => {
                const job = rowById[id];
                const isBoltOn = boltOnPicks.includes(id);
                const previous = index > 0 ? rowById[picks[index - 1]] : null;
                return (
                  <li key={id} className="relative pb-4 pl-8">
                    <span
                      className={`absolute left-[11px] top-0 h-full border-l-2 ${isBoltOn ? 'border-dashed border-zoddle-green/50' : 'border-gray-200'}`}
                      aria-hidden="true"
                    />
                    <span
                      className={`absolute left-0 top-1 inline-flex h-6 w-6 items-center justify-center rounded-full text-xs font-bold ${
                        isBoltOn ? 'border-2 border-dashed border-zoddle-green bg-white text-zoddle-green' : 'bg-zoddle-green text-white'
                      }`}
                    >
                      {index + 1}
                    </span>
                    <div className="flex items-start gap-2">
                      <div className="min-w-0 flex-1">
                        <div className="truncate text-sm font-semibold text-gray-900">{job?.customer_name || id}</div>
                        <div className="truncate text-xs text-gray-500">
                          {isBoltOn
                            ? previous
                              ? `Gets ${previous.customer_name || `stop ${index}`}'s leftovers`
                              : 'Needs a packed stop before it'
                            : [job?.scheduled_time, job?.area].filter(Boolean).join(' · ')}
                        </div>
                      </div>
                      <div className="flex shrink-0 items-center">
                        {picks.length > 1 && (
                          <>
                            <button type="button" onClick={() => onMove(index, -1)} disabled={index === 0}
                              aria-label="Visit earlier" className="rounded p-1 text-gray-400 hover:text-gray-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-zoddle-green disabled:opacity-25">↑</button>
                            <button type="button" onClick={() => onMove(index, 1)} disabled={index === picks.length - 1}
                              aria-label="Visit later" className="rounded p-1 text-gray-400 hover:text-gray-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-zoddle-green disabled:opacity-25">↓</button>
                          </>
                        )}
                        <button type="button" onClick={() => onToggle(id)} aria-label="Remove from route"
                          className="rounded p-1 text-gray-400 hover:text-red-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-red-400">✕</button>
                      </div>
                    </div>
                  </li>
                );
              })
            )}
            <RouteNode label="Back to warehouse" detail={picks.length ? 'One count, at reversal' : ''} last />
          </ol>
        </div>

        <div className="border-t border-gray-100 px-5 py-4">
          {picks.length > 0 && blockers.length > 0 && (
            <div className="mb-3 rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-800">{blockers[0]}</div>
          )}
          <button
            onClick={onSend}
            disabled={picks.length === 0 || blockers.length > 0 || sending}
            className="w-full rounded-lg bg-zoddle-green px-4 py-3 text-sm font-semibold text-white transition hover:opacity-90 focus:outline-none focus-visible:ring-2 focus-visible:ring-zoddle-green focus-visible:ring-offset-2 disabled:opacity-40"
          >
            {sendLabel}
          </button>
          {picks.length > 1 && (
            <div className="mt-2 text-center text-xs text-gray-400">
              {picks.length} customers · {bags} {bags === 1 ? 'bag' : 'bags'} · the rider accepts once
            </div>
          )}
        </div>
      </aside>
    </div>
  );
}

// The two ends of a route: the warehouse the bag leaves from and comes back to.
function RouteNode({ label, detail, last }) {
  return (
    <li className={`relative pl-8 ${last ? '' : 'pb-4'}`}>
      {!last && <span className="absolute left-[11px] top-3 h-full border-l-2 border-gray-200" aria-hidden="true" />}
      <span className="absolute left-[5px] top-1 h-3.5 w-3.5 rounded-sm border-2 border-gray-800 bg-white" aria-hidden="true" />
      <div className="text-xs font-semibold uppercase tracking-wide text-gray-800">{label}</div>
      {detail && <div className="text-xs text-gray-400">{detail}</div>}
    </li>
  );
}

export default function RiderAssignment() {
  const { navigate } = useNavigation();
  const [jobs, setJobs] = useState([]);
  const [riders, setRiders] = useState([]);
  // The rider is chosen once and used by every Assign on the board, single or trip.
  const [riderChoice, setRiderChoice] = useState('');
  const [mapLinks, setMapLinks] = useState({});
  // Appointment ids ticked to send together, in the order staff ticked them: for two or
  // more that order is the trip's visiting order.
  const [tripPicks, setTripPicks] = useState([]);
  // Unpacked orders on the board that take the previous stop's leftovers, and which
  // picked ids are those.
  const [boltOnCandidates, setBoltOnCandidates] = useState([]);
  const [boltOnPicks, setBoltOnPicks] = useState([]);
  const [busyId, setBusyId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const [tab, setTab] = useState('plan');
  const [overview, setOverview] = useState(null);
  const [endingRiderId, setEndingRiderId] = useState(null);
  const [cash, setCash] = useState(null);
  const [visits, setVisits] = useState([]);
  const [issues, setIssues] = useState([]);
  const [consoleError, setConsoleError] = useState(null);
  const [selectedBills, setSelectedBills] = useState({});
  const [collectingRiderId, setCollectingRiderId] = useState(null);
  const [collectNote, setCollectNote] = useState('');
  const [closing, setClosing] = useState(null);

  const riderById = useMemo(() => {
    const map = {};
    riders.forEach(rider => { map[rider.staff_id] = rider; });
    return map;
  }, [riders]);

  // A trip's stops sit together, in visiting order, however the server lists them.
  //
  // A stop the office closed is left off this tab: it is no longer work to assign or remove,
  // and it must not read as "ready" again while its bag is still out. Live Jobs keeps it on
  // its trip card ("Closed · bag still out") until the bag is reversed.
  const orderedJobs = useMemo(() => {
    const placed = new Set();
    const ordered = [];
    jobs.filter(job => !job.closed_by_office).forEach(job => {
      if (!job.offer_id || !job.trip_source_job_ids) {
        ordered.push(job);
        return;
      }
      if (placed.has(job.offer_id)) return;
      placed.add(job.offer_id);
      jobs
        .filter(other => other.offer_id === job.offer_id && !other.closed_by_office)
        .sort((a, b) => (a.stop_sequence || 0) - (b.stop_sequence || 0))
        .forEach(stop => ordered.push(stop));
    });
    return ordered;
  }, [jobs]);

  // Everything that stops the ticked jobs going out, in plain words, shown beside the
  // button instead of leaving it greyed out without a reason.
  const rowById = useMemo(() => {
    const map = {};
    [...jobs, ...boltOnCandidates].forEach(row => { map[row.appointment_id] = row; });
    return map;
  }, [jobs, boltOnCandidates]);

  const pickBlockers = [
    ...(riderChoice ? [] : ['Choose a rider']),
    ...(boltOnPicks.includes(tripPicks[0])
      ? ['The first stop needs its own pack; move a packed job first']
      : []),
    ...tripPicks
      .map(id => rowById[id])
      .filter(job => job && !(mapLinks[job.appointment_id] || '').trim())
      .map(job => `Add a map link for ${job.customer_name || 'a customer'}`),
  ];

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const [jobRows, riderRows, candidateRows] = await Promise.all([
        getRiderAssignmentJobs(),
        listAssignableRiders(),
        // Optional: a failure here only hides bolt-ons, never the board.
        getBoltOnCandidates().catch(() => []),
      ]);
      setBoltOnCandidates(candidateRows);
      setJobs(jobRows);
      setRiders(riderRows);
      const nextMapLinks = {};
      [...jobRows, ...candidateRows].forEach(job => {
        if (job.google_maps_link) nextMapLinks[job.appointment_id] = job.google_maps_link;
      });
      setMapLinks(nextMapLinks);
    } catch (err) {
      setError(errorMessage(err, 'Failed to load rider assignments'));
    } finally {
      setLoading(false);
    }
  }

  // The console sections load independently of the assignment table: if the
  // rider backend is down, the assignment tab must still render.
  async function loadConsole() {
    const [overviewRes, cashRes, visitsRes, issuesRes] = await Promise.allSettled([
      getRiderAdminOverview(),
      getRiderAdminCash(),
      getRiderAdminVisits(),
      getRiderAdminIssues(),
    ]);
    if (overviewRes.status === 'fulfilled') setOverview(overviewRes.value);
    if (cashRes.status === 'fulfilled') setCash(cashRes.value);
    if (visitsRes.status === 'fulfilled') setVisits(visitsRes.value.visits || []);
    if (issuesRes.status === 'fulfilled') setIssues(issuesRes.value.issues || []);

    const failed = [overviewRes, cashRes, visitsRes, issuesRes].find(r => r.status === 'rejected');
    setConsoleError(
      failed ? errorMessage(failed.reason, 'Rider service is unreachable') : null
    );
  }

  async function loadAll() {
    await Promise.all([load(), loadConsole()]);
  }

  useEffect(() => { loadAll(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  function toggleBill(checkoutId) {
    setSelectedBills(state => ({ ...state, [checkoutId]: !state[checkoutId] }));
  }

  function toggleAllBills(billIds, next) {
    setSelectedBills(state => {
      const updated = { ...state };
      billIds.forEach(id => { updated[id] = next; });
      return updated;
    });
  }

  async function endShift(rider) {
    const name = rider.full_name || 'this rider';
    if (!window.confirm(`End ${name}'s shift now? This closes it regardless of any open break or job.`)) return;
    setEndingRiderId(rider.rider_id);
    setError(null);
    setSuccess(null);
    try {
      await endRiderShift(rider.rider_id, undefined);
      setSuccess(`Ended ${name}'s shift.`);
      await loadConsole();
    } catch (err) {
      setError(errorMessage(err, 'Could not end shift'));
    } finally {
      setEndingRiderId(null);
    }
  }

  async function collectCash(holding, checkoutIds, total) {
    setCollectingRiderId(holding.rider_id);
    setError(null);
    setSuccess(null);
    try {
      await collectRiderCash(checkoutIds, collectNote);
      setSuccess(
        `Collected ₹${total} from ${holding.full_name || 'rider'} (${checkoutIds.length} bill${checkoutIds.length !== 1 ? 's' : ''}).`
      );
      setSelectedBills(state => {
        const updated = { ...state };
        checkoutIds.forEach(id => { delete updated[id]; });
        return updated;
      });
      setCollectNote('');
      // Refetch — collected bills drop out of the list.
      await loadConsole();
    } catch (err) {
      setError(errorMessage(err, 'Cash collection failed'));
    } finally {
      setCollectingRiderId(null);
    }
  }

  async function assign(job) {
    const riderId = riderChoice;
    const mapLink = (mapLinks[job.appointment_id] || '').trim();
    if (!riderId || !mapLink) return;
    setBusyId(job.appointment_id);
    setError(null);
    setSuccess(null);
    try {
      const result = await assignJobToRider(job.appointment_id, riderId, mapLink);
      const riderName = riderById[riderId]?.full_name || 'selected rider';
      setSuccess(
        result.offer_status === 'ACCEPTED'
          ? `${job.customer_name || 'Job'} assigned to ${riderName}. Their app has taken it.`
          : `${job.customer_name || 'Job'} offered to ${riderName}. Offer ${result.offer_id} expires at ${result.expires_at}.`
      );
      setTripPicks(picks => picks.filter(id => id !== job.appointment_id));
      await loadAll();
    } catch (err) {
      setError(errorMessage(err, 'Assign failed'));
    } finally {
      setBusyId(null);
    }
  }

  function toggleTripPick(appointmentId) {
    setTripPicks(picks => (
      picks.includes(appointmentId)
        ? picks.filter(id => id !== appointmentId)
        : [...picks, appointmentId]
    ));
    setBoltOnPicks(ids => ids.filter(id => id !== appointmentId));
  }

  // A bolt-on joins at the end of the trip, after the stop whose bag it will use.
  function toggleBoltOnPick(appointmentId) {
    if (tripPicks.includes(appointmentId)) {
      toggleTripPick(appointmentId);
      return;
    }
    setTripPicks(picks => [...picks, appointmentId]);
    setBoltOnPicks(ids => [...ids, appointmentId]);
  }

  function moveTripPick(index, delta) {
    setTripPicks(picks => {
      const target = index + delta;
      if (target < 0 || target >= picks.length) return picks;
      const next = [...picks];
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });
  }

  // One button for both: a single ticked job is a normal assign, two or more are a trip.
  function assignPicked() {
    if (tripPicks.length === 1) {
      const job = jobs.find(j => j.appointment_id === tripPicks[0]);
      if (job) assign(job);
      return;
    }
    assignTrip();
  }

  async function assignTrip() {
    if (tripPicks.length < 2 || !riderChoice) return;
    const links = Object.fromEntries(
      tripPicks.map(id => [id, (mapLinks[id] || '').trim()]).filter(([, link]) => link)
    );
    setBusyId('trip');
    setError(null);
    setSuccess(null);
    try {
      const result = await assignTripToRider(
        tripPicks, riderChoice, links, boltOnPicks.filter(id => tripPicks.includes(id))
      );
      const riderName = riderById[riderChoice]?.full_name || 'selected rider';
      setSuccess(
        result.offer_status === 'ACCEPTED'
          ? `Trip of ${tripPicks.length} assigned to ${riderName}. Their app has taken it.`
          : `Trip of ${tripPicks.length} offered to ${riderName}. Offer ${result.offer_id} expires at ${result.expires_at}.`
      );
      setTripPicks([]);
      setBoltOnPicks([]);
      await loadAll();
    } catch (err) {
      setError(errorMessage(err, 'Trip assign failed'));
    } finally {
      setBusyId(null);
    }
  }

  // One close action over the rider backend's two levers, picking whichever is true.
  // Completing a job the rider never started would credit them with a visit they did
  // not make; cancelling one they did make takes a completed job off their dashboard
  // and notifies them it was cancelled. `outcome` is only ever ZOCS's own resolved
  // answer -- never guessed here.
  async function closeRiderJobRow({ sourceJobId, jobStatus, label, outcome, reason, confirmMidVisit = true }) {
    const madeVisit = VISIT_HAPPENED_STATUSES.has(jobStatus);
    if (madeVisit && confirmMidVisit && !window.confirm(
      `${label} is mid-visit (${jobStatus.replace(/_/g, ' ').toLowerCase()}).\n\n`
      + 'Closing it now records the visit as completed on the rider app and releases '
      + 'the rider.\n\nContinue?'
    )) return;

    setBusyId(sourceJobId);
    setError(null);
    setSuccess(null);
    try {
      const result = madeVisit
        ? await completeRiderJob(sourceJobId, { outcome, reason })
        : await cancelRiderJob(sourceJobId, { reason, outcome });
      setSuccess(
        result.already_clear
          ? `${label} was already closed on the rider app.`
          : madeVisit
            ? `${label} closed as completed.`
            : `${label} taken off the rider.`
      );
      await loadAll();
    } catch (err) {
      setError(errorMessage(err, 'Could not close the rider job'));
    } finally {
      setBusyId(null);
    }
  }

  async function addBoltOn(job, candidate, link) {
    setBusyId(job.appointment_id);
    setError(null);
    setSuccess(null);
    try {
      await addBoltOnToDispatched(job.appointment_id, candidate.appointment_id, link);
      setSuccess(
        `${candidate.customer_name || 'The order'} is queued behind ${job.customer_name || 'the order'}. The rider has been told.`
      );
      await loadAll();
      return true;
    } catch (err) {
      setError(errorMessage(err, 'Could not add the bolt-on'));
      return false;
    } finally {
      setBusyId(null);
    }
  }

  function clearJob(job) {
    // The rider backend withdraws a not-yet-accepted trip whole, since the rider never
    // agreed to a different trip; once accepted only this stop comes off.
    if (job.trip_source_job_ids && !window.confirm(
      `${job.customer_name || 'This job'} is stop ${job.stop_sequence} of a ${job.trip_source_job_ids.length}-stop trip.\n\n`
      + 'If the rider has not accepted the trip yet, removing this stop withdraws the WHOLE trip. '
      + 'If they have, only this stop is removed and the rest of the trip continues.\n\nContinue?'
    )) return undefined;
    return closeRiderJobRow({
      sourceJobId: job.source_job_id,
      jobStatus: job.job_status,
      label: job.customer_name || 'Job',
      // The assignment board only holds appointments still dispatched, so ZOCS has not
      // resolved an outcome yet. Sending none leaves the rider backend's own untouched.
      reason: 'Removed by the warehouse',
    });
  }

  function closeVisit(visit) {
    setClosing(visit);
  }

  async function confirmClose(visit, reasonText, outcome) {
    await closeRiderJobRow({
      sourceJobId: visit.source_job_id,
      jobStatus: visit.status,
      label: visit.customer_name || visit.source_job_id,
      // Lets ZOCS-closed appointments keep their own wording; everything else carries the
      // reason ops picked in the dialog.
      reason: visit.zocs_closed ? 'Order already closed at warehouse' : reasonText,
      outcome,
      confirmMidVisit: false,
    });
    setClosing(null);
  }

  return (
    <div className="p-6">
      <div className="flex items-start justify-between gap-4 mb-5">
        <div>
          <h2 className="text-xl font-bold text-gray-800">Rider Operations</h2>
          <p className="text-sm text-gray-400 mt-1">
            Assign dispatched appointments, watch what riders are doing, and collect the cash
            they are carrying.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={loadAll}
            className="text-sm font-semibold text-gray-500 hover:text-zoddle-green transition-colors"
          >
            Refresh
          </button>
          <button
            onClick={() => navigate('intransit')}
            className="text-sm font-semibold border border-gray-200 text-gray-600 px-3 py-2 rounded hover:bg-gray-50"
          >
            Back to In Transit
          </button>
        </div>
      </div>

      {error && (
        <div className="mb-4 bg-red-50 border border-red-100 text-zoddle-pink text-sm px-4 py-3 rounded">
          {error}
        </div>
      )}
      {success && (
        <div className="mb-4 bg-green-50 border border-green-100 text-green-700 text-sm px-4 py-3 rounded">
          {success}
        </div>
      )}

      {consoleError && (
        <div className="mb-4 bg-amber-50 border border-amber-200 text-amber-800 text-sm px-4 py-3 rounded">
          Rider service: {consoleError} — assignment still works, live rider data may be stale.
        </div>
      )}

      <CountsStrip overview={overview} />

      <div className="flex flex-wrap gap-2 border-b border-gray-200 mb-5">
        {[
          { key: 'plan', label: 'Assign', count: jobs.filter(job => !hasRiderOn(job) && job.operational_status !== 'queued').length },
          {
            key: 'live-jobs',
            label: 'Live Jobs',
            count: visits.filter(v => !v.finished).length,
            alert: visits.filter(v => v.zocs_closed && !v.finished).length,
          },
          { key: 'board', label: 'Rider Board', count: overview?.riders?.length || 0 },
          { key: 'cash', label: 'Cash', count: cash?.holdings?.length || 0 },
          {
            key: 'entry-codes',
            label: 'Entry Codes',
            count: visits.filter(v => v.entry_decline_code).length,
          },
          { key: 'issues', label: 'Issues', count: issues.filter(issue => issue.status !== 'RESOLVED').length },
        ].map(item => (
          <button
            key={item.key}
            onClick={() => setTab(item.key)}
            className={`text-sm font-semibold px-4 py-2 -mb-px border-b-2 transition-colors ${
              tab === item.key
                ? 'border-zoddle-green text-zoddle-green'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            {item.label}
            {item.count > 0 && (
              <span className="ml-1.5 text-xs text-gray-400">{item.count}</span>
            )}
            {item.alert > 0 && (
              <span className="ml-1.5 text-xs font-bold text-amber-600">⚠ {item.alert}</span>
            )}
          </button>
        ))}
      </div>

      {loading && <div className="text-gray-500">Loading...</div>}

      {tab === 'board' && !loading && (
        <RiderBoard overview={overview} onEndShift={endShift} endingRiderId={endingRiderId} />
      )}

      {tab === 'entry-codes' && !loading && <EntryCodesBoard visits={visits} />}

      {tab === 'issues' && !loading && <IssuesBoard issues={issues} onChanged={loadConsole} />}

      {tab === 'cash' && !loading && (
        <div>
          <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
            <p className="text-sm text-gray-500 max-w-xl">
              The customer has already paid — this records the notes moving from a rider's
              pocket into the till. Collecting twice is safe; it keeps the first record.
            </p>
            <input
              value={collectNote}
              onChange={e => setCollectNote(e.target.value)}
              placeholder="Note (e.g. end of shift)"
              className="text-sm border border-gray-200 rounded px-3 py-2 w-64 focus:outline-none focus:border-zoddle-green"
            />
          </div>
          {(cash?.holdings || []).length === 0 ? (
            <div className="text-gray-500 italic py-8 text-center bg-white border border-gray-200 rounded">
              No rider is carrying uncollected cash.
            </div>
          ) : (
            <>
              <div className="mb-3 text-sm text-gray-600">
                <strong className="text-gray-900">₹{cash.total}</strong> out with{' '}
                {cash.holdings.length} rider{cash.holdings.length !== 1 ? 's' : ''}.
              </div>
              {cash.holdings.map(holding => (
                <CashHolding
                  key={holding.rider_id}
                  holding={holding}
                  selected={selectedBills}
                  onToggleBill={toggleBill}
                  onToggleAll={toggleAllBills}
                  onCollect={collectCash}
                  busy={collectingRiderId === holding.rider_id}
                />
              ))}
            </>
          )}
        </div>
      )}

      {tab === 'live-jobs' && !loading && (
        <LiveJobsBoard visits={visits} onClose={closeVisit} busyId={busyId} />
      )}

      {closing && (
        <CloseStopDialog
          visit={closing}
          visits={visits}
          busy={busyId === closing.source_job_id}
          onCancel={() => setClosing(null)}
          onConfirm={confirmClose}
        />
      )}

      {tab === 'plan' && !loading && (
        <PlanBoard
          jobs={orderedJobs}
          rowById={rowById}
          boltOnCandidates={boltOnCandidates}
          boltOnPicks={boltOnPicks}
          onToggleBoltOn={toggleBoltOnPick}
          riders={riders}
          riderChoice={riderChoice}
          setRiderChoice={setRiderChoice}
          picks={tripPicks}
          onToggle={toggleTripPick}
          onMove={moveTripPick}
          onSend={assignPicked}
          onRemove={clearJob}
          onAddBoltOn={addBoltOn}
          mapLinks={mapLinks}
          setMapLinks={setMapLinks}
          blockers={pickBlockers}
          busyId={busyId}
        />
      )}

    </div>
  );
}
